import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { TableTopPanel } from 'components/TableTopPanel';
import { AG_GRID_ICON_SETUP } from 'constantsProject';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type {
    BimChapterEnum,
    BimPriceLevelEnum,
    BimWorkOrderPriceData,
    ContractStatusEnum,
    ProjectStatusEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { getCheckedIdsArr } from 'utils';

import type { GridReadyEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { ForwardedRef } from 'react';
import type React from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';

import { BimWorkOrderPriceRowDetails } from '../../BimCommonComponents/BimWorkOrderPriceRowDetails';
import { BimWorkOrderPriceRevisions } from '../../WorkOrder/BimWorkOrderPriceRevisions/BimWorkOrderPriceRevisions';
import Style from '../../WorkOrder/BimWorkOrderPriceTable/BimWorkOrderPriceTable.module.css';
import type { IBimWorkOrderRowDetails } from '../../WorkOrder/BimWorkOrderPriceTable/cellRenderers';
import { getBimWorkOrderRowClass } from '../../WorkOrder/BimWorkOrderPriceTable/cellRenderers';
import { useBimWorkOrderPriceRevisionsFilter } from '../../WorkOrder/BimWorkOrderPriceTable/hooks';
import { BimWorkOrderRowDetailsType, type BimWorkOrderRowTypeEnum } from '../../WorkOrder/types';
import { useBimWorkOrderVersionPriceStates } from '../hooks/useBimWorkOrderVersionPriceStates';

import { BimWorkOrderVersionActsKc2 } from './BimWorkOrderVersionActsKc2/BimWorkOrderVersionActsKc2';
import { useBimWorkOrderPriceVersionDetails } from './hooks/useBimWorkOrderPriceVersionDetails';
import { useBimWorkOrderVersionPrice } from './hooks/useBimWorkOrderVersionPrice';
import { useBimWorkOrderVersionTotalsRow } from './hooks/useBimWorkOrderVersionTotalsRow';
import { useWorkOrderVersionColumns } from './hooks/useWorkOrderVersionColumns';

const defaultColDef = {
    resizable: true,
    wrapHeaderText: true,
};

interface IBimWorkOrderPriceTable {
    workOrderId: number;
    workOrderRootObjectId: number | undefined;
    chapter: BimChapterEnum | undefined;
    constructionTypeId: number | undefined;
    totalChapterDetailsRowOpenOuter: { rowType: BimWorkOrderRowTypeEnum.TotalsString; data: null } | null;
    toggleTotalChapterDetailsOuter: () => void;
    makeRefreshTable: () => void;
    hasInProgress: boolean;
    isFrameContract: boolean;
    includeRows?: boolean;
    workOrderStatus?: WorkOrderStatusEnum;
    resetCheckedRowsRef: React.MutableRefObject<(() => void) | undefined>;
    contractStatus?: ContractStatusEnum;
    orderRules?: IUseAccess['orderBim'];
    statusProject?: ProjectStatusEnum;
    workOrderVersionId: number;
    isVersionPage?: boolean;
    priceLevel: BimPriceLevelEnum | undefined;
}

export const BimWorkOrderPriceVersionTable = forwardRef(
    (props: IBimWorkOrderPriceTable, ref: ForwardedRef<AgGridReact>) => {
        const {
            workOrderRootObjectId,
            workOrderId,
            workOrderVersionId,
            chapter,
            includeRows = false,
            constructionTypeId,
            totalChapterDetailsRowOpenOuter,
            toggleTotalChapterDetailsOuter,
            makeRefreshTable: makeRefresh,
            workOrderStatus,
            resetCheckedRowsRef,
            contractStatus,
            orderRules,
            statusProject,
            hasInProgress,
            isFrameContract,
            isVersionPage,
            priceLevel,
        } = props;

        const tableKey = useMemo(
            () => `${chapter}_${workOrderRootObjectId}-${priceLevel}`,
            [chapter, workOrderRootObjectId, priceLevel],
        );

        const {
            bimWorkOrderPriceVersionStatesData: { hasExcessCompletedTotalSMR },
            bimWorkOrderPriceStatesMakeRequest,
        } = useBimWorkOrderVersionPriceStates(chapter, workOrderRootObjectId);

        const makeRefreshTable = useCallback(() => {
            makeRefresh();
            bimWorkOrderPriceStatesMakeRequest();
        }, [bimWorkOrderPriceStatesMakeRequest, makeRefresh]);

        const {
            fullSetColumns,
            detailsColumnsByType,
            detailsTitle,
            closeDetailsModalAndCleanChecked,
            checkedPriceRowForDetails,
            fullSetSettingsColumns,
            onFullSetTableSettingsConfirm,
            isFullSetColumnVisible,
            resetCheckedRows,
            checkedRows,
            filterValues,
            regularDetailsFilterValues,
            isTotalDetails,
        } = useWorkOrderVersionColumns({
            workOrderRootObjectId,
            chapter,
            totalChapterDetailsRowOpenOuter,
            toggleTotalChapterDetailsOuter,
            makeRefreshTable,
            workOrderStatus,
            contractStatus,
            hasExcessCompletedTotalSMR,
            orderRules,
            statusProject,
            hasInProgress,
            isFrameContract,
            workOrderVersionId,
        });

        const { bimPriceListDataSource, priceListCounter, fetchedItemsCounter } = useBimWorkOrderVersionPrice({
            workOrderVersionId,
            workOrderVersionRootObjectId: workOrderRootObjectId,
            chapter,
            includeRows,
            filterValues,
            priceLevel,
        });

        const {
            filterValues: revisionsFilterValues,
            changeFilterHandler: changeRevisionsFilterHandler,
            clearFilter: clearRevisionsFilter,
        } = useBimWorkOrderPriceRevisionsFilter();

        const { detailsData, isDetailsLoading, detailsBaseRow } = useBimWorkOrderPriceVersionDetails({
            checkedPriceRowForDetails,
            chapter,
            workOrderObjectId: workOrderRootObjectId,
            constructionTypeId,
            totalChapterDetailsRowOpenOuter,
            regularDetailsFilterValues,
            workOrderVersionId,
            priceLevel,
        });

        const { rowTotalsData } = useBimWorkOrderVersionTotalsRow({
            chapter,
            constructionTypeId,
            workOrderVersionId,
            workOrderObjectId: workOrderRootObjectId,
            priceLevel,
        });

        const memoizedRevisionsRequestIds = useMemo(
            () => ['bimWorkOrderPriceRevisionCodeFilter', 'bimWorkOrderPriceRevisionNameFilter'],
            [],
        );

        const [resetRevisionsFiltersData] = useResetMultipleStoreData({
            requestIds: memoizedRevisionsRequestIds,
        });

        useImperativeHandle(resetCheckedRowsRef, () => resetCheckedRows, [resetCheckedRows]);

        useEffect(
            () => () => {
                resetCheckedRows();
            },
            [resetCheckedRows, workOrderId, workOrderRootObjectId, chapter],
        );

        useEffect(() => {
            if (typeof ref !== 'function' && ref?.current) {
                if (!priceListCounter) {
                    ref?.current?.api?.showNoRowsOverlay();
                } else {
                    ref?.current?.api?.hideOverlay();
                }
            }
        }, [priceListCounter, ref]);

        const onGridReady = useCallback((event: GridReadyEvent) => {
            event.api.showNoRowsOverlay();
        }, []);

        const onActsClose = useCallback(() => {
            closeDetailsModalAndCleanChecked();
            makeRefreshTable();
        }, [closeDetailsModalAndCleanChecked, makeRefreshTable]);

        const onRevisionsClose = useCallback(() => {
            closeDetailsModalAndCleanChecked();
            clearRevisionsFilter();
            resetRevisionsFiltersData();
        }, [clearRevisionsFilter, closeDetailsModalAndCleanChecked, resetRevisionsFiltersData]);

        const isShowDetails = useMemo(
            () =>
                (checkedPriceRowForDetails &&
                    checkedPriceRowForDetails.detailsType === BimWorkOrderRowDetailsType.Details) ||
                totalChapterDetailsRowOpenOuter,
            [checkedPriceRowForDetails, totalChapterDetailsRowOpenOuter],
        );

        const isShowHistoryDetails = useMemo(
            () =>
                checkedPriceRowForDetails?.detailsType &&
                [BimWorkOrderRowDetailsType.Update, BimWorkOrderRowDetailsType.Revision].includes(
                    checkedPriceRowForDetails?.detailsType,
                ),
            [checkedPriceRowForDetails],
        );

        const isShowActsKc2 = useMemo(
            () =>
                (checkedPriceRowForDetails &&
                    checkedPriceRowForDetails.detailsType === BimWorkOrderRowDetailsType.ActsKc2) ||
                totalChapterDetailsRowOpenOuter,
            [checkedPriceRowForDetails, totalChapterDetailsRowOpenOuter],
        );

        return (
            <>
                <TableTopPanel
                    checkedRowsArr={getCheckedIdsArr(checkedRows)}
                    resetCheckedRows={resetCheckedRows}
                    totalItems={priceListCounter}
                    fetchedItems={fetchedItemsCounter}
                    optionalColumns={fullSetSettingsColumns}
                    handleTableSettingsConfirm={onFullSetTableSettingsConfirm}
                    isColumnVisible={isFullSetColumnVisible}
                    areChildrenOnLeftSide
                    showPinColumn
                    tableApi={(ref as any)?.current}
                />
                <div className={`ag-theme-alpine BimWorkOrderPrice ${Style.bimWorkOrderPrice} customHeaderColor`}>
                    <AgGridTable
                        datasource={bimPriceListDataSource}
                        columnDefs={fullSetColumns}
                        overlayNoRowsTemplate="Для отображения данных загрузите сметы"
                        rowModelType="infinite"
                        tooltipShowDelay={200}
                        maxConcurrentDatasourceRequests={1}
                        maxBlocksInCache={50}
                        cacheBlockSize={50}
                        defaultColDef={defaultColDef}
                        pinnedBottomRowData={rowTotalsData}
                        rowClassRules={getBimWorkOrderRowClass}
                        onGridReady={onGridReady}
                        ref={ref}
                        icons={AG_GRID_ICON_SETUP}
                        suppressDragLeaveHidesColumns
                        context={{
                            tableUniqueId: `${chapter}_${workOrderRootObjectId}`,
                        }}
                        key={tableKey}
                    />
                </div>
                {isShowDetails && (
                    <BimWorkOrderPriceRowDetails
                        modalTitle={detailsTitle}
                        baseRow={detailsBaseRow}
                        data={detailsData}
                        columns={detailsColumnsByType}
                        onClose={closeDetailsModalAndCleanChecked}
                        isDetailsLoading={isDetailsLoading}
                        defaultColDef={defaultColDef}
                        styleClass="BimWorkOrderPriceDetails customHeaderColor"
                        rowClassRules={getBimWorkOrderRowClass}
                        suppressDragLeaveHidesColumns={!isTotalDetails}
                        priceLevel={isTotalDetails ? undefined : priceLevel}
                    />
                )}
                {isShowHistoryDetails && checkedPriceRowForDetails && (
                    <BimWorkOrderPriceRevisions
                        onClose={onRevisionsClose}
                        columns={fullSetColumns}
                        baseRow={detailsBaseRow}
                        filterValues={revisionsFilterValues}
                        changeFilterHandler={changeRevisionsFilterHandler}
                        selectedRow={checkedPriceRowForDetails as IBimWorkOrderRowDetails<BimWorkOrderPriceData>}
                        rowClassRules={getBimWorkOrderRowClass}
                        defaultColDef={defaultColDef}
                        styleClass="BimWorkOrderPriceDetails customHeaderColor"
                        isVersionPage={isVersionPage}
                        priceLevel={priceLevel}
                    />
                )}
                {isShowActsKc2 && checkedPriceRowForDetails && (
                    <BimWorkOrderVersionActsKc2
                        onClose={onActsClose}
                        baseRow={detailsBaseRow}
                        selectedRow={checkedPriceRowForDetails as IBimWorkOrderRowDetails<BimWorkOrderPriceData>}
                        rowClassRules={getBimWorkOrderRowClass}
                        defaultColDef={defaultColDef}
                        styleClass="BimWorkOrderPriceDetails customHeaderColor"
                        workOrderStatus={workOrderStatus}
                        contractStatus={contractStatus}
                        statusProject={statusProject}
                        isVersionPage={isVersionPage}
                        priceLevel={priceLevel}
                    />
                )}
            </>
        );
    },
);

BimWorkOrderPriceVersionTable.displayName = 'BimWorkOrderPriceVersionTable';
